<template>
    <div class="product-boost-header">
        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>

        <div v-else-if="product" class="product-header-content">
            <div class="row align-items-start gap-0">
                <!-- Product Image -->
                <div class="col-auto">
                    <div class="product-image-wrapper">
                        <img :src="productImage" :alt="product.name || product.title" class="product-image" />
                        <div v-if="product.boost_status || product.is_boosted" class="boost-badge">
                            <span class="mdi mdi-rocket"></span>
                        </div>
                    </div>
                </div>

                <!-- Product Info -->
                <div class="col ps-3 ps-md-4">
                    <div class="product-info-wrapper">
                        <h3 class="product-name mb-3">{{ product.name || product.title }}</h3>

                        <div class="product-meta-container mb-3">
                            <span v-if="product.category" class="badge badge-soft-primary me-2 mb-2">
                                <span class="mdi mdi-tag-outline me-1"></span>
                                {{ product.category }}
                            </span>

                            <span v-if="product.price" class="badge badge-soft-success me-2 mb-2">
                                <span class="mdi mdi-currency-usd me-1"></span>
                                {{ formatPrice(product.price) }}
                            </span>

                            <span v-if="product.location" class="badge badge-soft-info me-2 mb-2">
                                <span class="mdi mdi-map-marker-outline me-1"></span>
                                {{ product.location }}
                            </span>
                        </div>

                        <p v-if="product.description" class="product-description mb-3">
                            {{ truncateDescription(product.description) }}
                        </p>

                        <!-- Boost Status -->
                        <div v-if="product.boost_status || product.is_boosted" class="alert alert-info boost-alert mb-0" role="alert">
                            <span class="mdi mdi-rocket-launch me-2"></span>
                            <strong>{{ $t('boost.currentlyBoosted') }}</strong>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="text-center py-5">
            <div class="no-product-info">
                <span class="mdi mdi-information-outline"></span>
                <p class="mb-0 mt-2">{{ $t('product.noInfoAvailable') }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import helper from "@/services/helper";
import config from "@/config";
import img_not_found from "@/assets/img/img_not_found.jpg";

export default {
    name: 'ProductHeader',
    data() {
        return {
            storageUrl: config.storageUrl,
            img_not_found: img_not_found,
        };
    },
    props: {
        product: {
            type: Object,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        },
        maxDescriptionLength: {
            type: Number,
            default: 150
        }
    },
    computed: {
        productImage() {
            if (!this.product) return this.img_not_found;
            
            // Handle different image structures
            if (this.product.pictures && Array.isArray(this.product.pictures) && this.product.pictures.length > 0) {
                // Construct full URL if needed
                const imagePath = config.storageUrl + this.product.pictures[0];
                return imagePath.startsWith('http') ? imagePath : `/${imagePath}`;
            }
            
            // Fallback placeholder
            return this.img_not_found;
        }
    },
    methods: {
        formatPrice: helper.formatPrice,

        truncateDescription(description) {
            if (!description) return '';
            if (description.length <= this.maxDescriptionLength) {
                return description;
            }
            return description.substring(0, this.maxDescriptionLength) + '...';
        }
    }
};
</script>

<style scoped>
.product-boost-header {
    background: #ffffff;
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 24px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
    border: 1px solid #f0f0f0;
    transition: box-shadow 0.3s ease;
}

.product-boost-header:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
}

.product-image-wrapper {
    position: relative;
    width: 140px;
    height: 140px;
    border-radius: 12px;
    overflow: hidden;
    background: linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.product-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
}

.product-image-wrapper:hover .product-image {
    transform: scale(1.05);
}

.boost-badge {
    position: absolute;
    top: 8px;
    right: 8px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0%, 100% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.1);
    }
}

.boost-badge .mdi {
    font-size: 18px;
}

.product-info-wrapper {
    min-width: 0;
}

.product-name {
    font-size: 22px;
    font-weight: 700;
    color: #1a202c;
    line-height: 1.4;
    letter-spacing: -0.02em;
}

.product-meta-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0;
}

.badge-soft-primary {
    background-color: #e3f2fd;
    color: #1976d2;
    font-weight: 500;
    padding: 6px 12px;
    font-size: 13px;
}

.badge-soft-success {
    background-color: #e8f5e9;
    color: #388e3c;
    font-weight: 600;
    padding: 6px 12px;
    font-size: 13px;
}

.badge-soft-info {
    background-color: #e0f7fa;
    color: #0097a7;
    font-weight: 500;
    padding: 6px 12px;
    font-size: 13px;
}

.product-description {
    font-size: 14px;
    color: #64748b;
    line-height: 1.7;
}

.boost-alert {
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    border: 1px solid #90caf9;
    border-radius: 8px;
    padding: 10px 16px;
    font-size: 13px;
    color: #1565c0;
}

.boost-alert .mdi {
    font-size: 18px;
    vertical-align: middle;
}

.no-product-info {
    color: #94a3b8;
}

.no-product-info .mdi {
    font-size: 64px;
    opacity: 0.3;
    display: block;
}

.no-product-info p {
    font-size: 14px;
    color: #64748b;
}

/* Responsive */
@media (max-width: 576px) {
    .product-boost-header {
        padding: 20px;
    }

    .product-header-content .row {
        /* align-items: center; */
        justify-content: flex-start;
        text-align: center;
    }

    .product-header-content .col {
        padding-left: 0 !important;
    }

    .product-image-wrapper {
        width: 120px;
        height: 120px;
    }

    .product-name {
        font-size: 20px;
    }

    .product-meta-container {
        justify-content: center;
    }
}

@media (max-width: 768px) {
    .product-image-wrapper {
        width: 120px;
        height: 120px;
    }

    .product-name {
        font-size: 20px;
    }
}
</style>